import forge from 'apis/forge';
import InviteCode from 'components/Home/InviteCode';
import ProjectCreator from 'components/Home/Creator/ProjectCreator';
import DesignSystemCreator from 'components/Home/Creator/DesignSystemCreator';
import ModalManager from 'components/ModalManager';
import Profile from 'components/Profile';
import SceneLayout from 'components/SceneLayout';
import DesignSystemLayout from 'components/DesignSystemLayout';
import Sidebar from 'components/Sidebar';
import StaleAppDetector from 'components/StaleAppDetector';
import StandaloneFrameViewer from 'components/StandaloneFrameViewer';
import GlobalContextMenu from 'components/Widget/GlobalContextMenu';
import ErrorOverlay from 'components/Widget/Overlay/ErrorOverlay';
import LoadingOverlay from 'components/Widget/Overlay/LoadingOverlay';
import SidebarToggle from 'components/Widget/Button/SidebarToggle';
import { AuthUser, DesignSystemMetadata, SceneMetadata } from 'dragonmouse-shared/interfaces';
import useNoGestures from 'hooks/useNoGestures';
import { useSidebarWidth } from 'hooks/useSidebarWidth';
import { useEffect, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
	BrowserRouter,
	matchPath,
	Outlet,
	Route,
	Routes,
	useLocation,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import {
	selectActiveDesignSystem,
	selectActiveScene,
	selectIsGuest,
	selectScenes,
	selectUser,
} from 'redux/db/reducers';
import { updateSubscription } from 'redux/db/actions';
import { selectIsMobile } from 'redux/mode/reducers';
import { canEdit, getFrame } from 'redux/db/util';
import {
	setActiveSceneId,
	setActiveDesignSystemId,
	setAuthUser,
	setDeviceConfig,
	setSidebarVisible,
	setSystemDarkMode,
} from 'redux/mode/actions';
import { startListeningForEvents } from 'redux/mode/util';
import { RootReducer, selectIsAppReady, selectIsLoadingAnything } from 'redux/reducers';
import Smartlook from 'smartlook-client';
import { ThemeProvider } from 'styled-components';
import { darkTheme, lightTheme } from 'tokens';
import {
	initAnalytics,
	trackGoogleAdsConversion,
	trackOutputAction,
	trackPageView,
} from '../analytics';
import * as routes from '../routes';
import {
	detectDeviceConfig,
	hasEarlyAccess,
	hasGuestOrEarlyAccess,
	mixpanelIdentify,
} from '../utils';
import * as S from './styles';

function AppContent() {
	const {
		darkMode,
		loading,
		sceneLoadError,
		activeScene,
		activeDesignSystem,
		authUser,
		user,
		sidebarVisible,
		sidebarDocked,
		isMobile,
		scenes,
		isAppReady,
		sidebarVisibilityInitialized,
		isGuest,
	} = useSelector((r: RootReducer) => {
		return {
			darkMode: r.mode.darkMode,
			loading: selectIsLoadingAnything(r),
			sceneLoadError: r.db.sceneLoadError,
			activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			authUser: r.mode.authUser as AuthUser | null,
			user: selectUser(r.db),
			sidebarVisible: r.mode.sidebarVisible,
			sidebarDocked: r.mode.sidebarDocked,
			isMobile: selectIsMobile(r.mode),
			scenes: selectScenes(r.db),
			isAppReady: selectIsAppReady(r),
			sidebarVisibilityInitialized: r.mode.sidebarVisibilityInitialized,
			isGuest: selectIsGuest(r.db),
		};
	}, shallowEqual);

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const sidebarWidth = useSidebarWidth();
	const [searchParams] = useSearchParams();
	const frameIdParam = searchParams.get('frameId');
	const sharedParam = searchParams.get('shared');
	const hasProcessedSharedLink = useRef(false);

	const hasAccess = hasGuestOrEarlyAccess(authUser);

	// Route detection using matchPath with route patterns as single source of truth
	const isBaseRoute = matchPath(routes.home(), location.pathname) !== null;
	const isSceneRoute = matchPath(routes.scenePattern, location.pathname) !== null;
	const hasProjects = scenes.some((s) => !s.viewOnly);

	useEffect(() => {
		if (activeScene) {
			// if the activeScene is not the same as the current URL, navigate to the new scene
			// Use frame title if frameId is present, otherwise use scene title
			const frame = frameIdParam ? getFrame(frameIdParam) : null;
			const title = frame ? frame.title : activeScene.title;
			document.title = `Moonchild - ${title}`;

			const expectedScenePath = routes.scene(activeScene.id, activeScene.title);
			const currentPath = window.location.pathname;

			// Only redirect to fix scene path if we're actually on a scene route.
			// This prevents redirecting back to the scene when navigating away to
			// other routes (design system, profile, etc.) while activeScene is still set.
			if (isSceneRoute && currentPath !== expectedScenePath) {
				// Preserve query parameters when navigating to scene
				const currentSearch = window.location.search;
				const targetPath = currentSearch
					? `${expectedScenePath}${currentSearch}`
					: expectedScenePath;
				navigate(targetPath, { replace: true });
			}
		} else {
			document.title = 'Moonchild';
		}
	}, [activeScene?.id, activeScene?.title, frameIdParam, isSceneRoute]);

	useEffect(() => {
		// when a scene is loaded, mark the opening time in the scene metadata (once per user per scene)
		if (!activeScene?.id || !user?.id) return;

		const existingOpen = (activeScene.metadata as SceneMetadata)?.openActions?.[user.id];
		const date = new Date();

		// If existingOpen time is within a minute, don't update it (to prevent repeated calls of the updateScene endpoint)
		if (existingOpen && date.getTime() - new Date(existingOpen).getTime() < 60000) return;

		// I spent a while trying to put this in canvas, strange things started to happen. Putting it here seems to work - ss
		if (canEdit(activeScene)) {
			forge.updateScene({
				update: {
					id: activeScene.id,
					metadata: {
						...(activeScene.metadata as SceneMetadata),
						openActions: { ...(activeScene?.metadata?.openActions || {}), [user.id]: date },
					},
				},
			});
		}
	}, [activeScene?.id, user?.id]);

	useEffect(() => {
		// when a design system is loaded, mark the opening time in the design system metadata (once per user per design system)
		if (!activeDesignSystem?.id || !user?.id) return;

		const existingOpen = (activeDesignSystem.metadata as DesignSystemMetadata)?.openActions?.[user.id];
		const date = new Date();

		// If existingOpen time is within a minute, don't update it (to prevent repeated calls of the updateDesignSystem endpoint)
		if (existingOpen && date.getTime() - new Date(existingOpen).getTime() < 60000) return;

		forge.updateDesignSystem({
			update: {
				id: activeDesignSystem.id,
				metadata: {
					...(activeDesignSystem.metadata as DesignSystemMetadata),
					openActions: {
						...(activeDesignSystem?.metadata?.openActions || {}),
						[user.id]: date,
					},
				},
			},
		});
	}, [activeDesignSystem?.id, user?.id]);

	useEffect(() => {
		(async () => {
			startListeningForEvents();
		})().catch(console.error);
	}, [dispatch]);

	useEffect(() => {
		const userTheme = window.matchMedia('(prefers-color-scheme: dark)');
		dispatch(setSystemDarkMode(userTheme.matches));

		const handleThemeChange = (event: { matches: boolean }) => {
			dispatch(setSystemDarkMode(event.matches));
		};
		userTheme.addEventListener('change', handleThemeChange);

		return () => {
			userTheme.removeEventListener('change', handleThemeChange);
		};
	}, [dispatch]);

	const baseRef = useRef() as React.MutableRefObject<HTMLDivElement>;

	// quite surprisingly, disabling gestures here doesn't preclude us from selectively
	// enabling gestures on components (such as the canvas) that are App's children
	useNoGestures(baseRef);

	// Things to do on mount
	useEffect(() => {
		// Detect device configuration
		const deviceConfig = detectDeviceConfig();
		dispatch(setDeviceConfig(deviceConfig));

		// Load the user
		async function fetchUser() {
			const authUser = await forge.getAuthUser();
			dispatch(setAuthUser(authUser as AuthUser));

			if (hasEarlyAccess(authUser)) {
				// to avoid throwing a permissions error, we only initialize the user if they have early_access
				// [note users who acquire early access permission during a session need to be initialized when they acquire early access permission]
				const { userCreated } = await forge.initializeUser();
				if (userCreated) {
					console.log('*- app -*');
					trackGoogleAdsConversion();
				}
			}

			if (authUser.id === 'guest') {
				dispatch(updateSubscription(null));
			} else {
				try {
					await forge.getSubscription();
				} catch (error) {
					console.error('Failed to load subscription', error);
				}
			}
		}
		fetchUser();

		const navigateListener = (event: CustomEvent) => {
			const path = event.detail as string;
			navigate(path);
		};

		window.addEventListener('studio-navigate', navigateListener);

		return () => {
			window.removeEventListener('studio-navigate', navigateListener);
		};
	}, []);

	useEffect(() => {
		// Initialize Smartlook
		if (authUser?.email) {
			if (!Smartlook.initialized()) {
				Smartlook.init('147e55c2100638320f5af4a5d6d42983f44150aa');
			}

			Smartlook.identify(authUser.id, { email: authUser.email, name: authUser.name });
		}

		// Identify user in Mixpanel
		if (authUser?.id && typeof window.mixpanel !== 'undefined') {
			window.mixpanel.identify(authUser.id);
			mixpanelIdentify({
				$email: authUser.email,
				$name: authUser.name,
				$created: new Date().toISOString(),
				$invite_codes: authUser.inviteCodes,
			});
		}
	}, [authUser]);

	// Migrate browser settings to account (one-time)
	// Read directly from localStorage to check for old values that may not be in Settings type anymore
	useEffect(() => {
		if (!user || isGuest || !authUser || authUser.id === 'guest') {
			return;
		}

		try {
			const jsonText = localStorage.getItem('mode');
			if (!jsonText) {
				return;
			}

			const saved = JSON.parse(jsonText);
			const oldSettings = saved?.settings;
			if (!oldSettings) {
				return;
			}

			const existingSettings = user.metadata?.settings || {};
			const settingsToMigrate: { visualEditsEnabled?: boolean; designSystemsEnabled?: boolean } =
				{};

			// Only migrate values that exist in oldSettings but not in account settings
			if (
				oldSettings.visualEditsEnabled !== undefined &&
				existingSettings.visualEditsEnabled === undefined
			) {
				settingsToMigrate.visualEditsEnabled = oldSettings.visualEditsEnabled;
			}

			if (
				oldSettings.designSystemsEnabled !== undefined &&
				existingSettings.designSystemsEnabled === undefined
			) {
				settingsToMigrate.designSystemsEnabled = oldSettings.designSystemsEnabled;
			}

			if (Object.keys(settingsToMigrate).length > 0) {
				forge.updateUser({
					update: {
						id: user.id,
						metadata: {
							settings: {
								...existingSettings,
								...settingsToMigrate,
							},
						},
					},
				});
			}
		} catch (error) {
			// Silently fail migration if localStorage is corrupted
			console.error('Failed to migrate settings:', error);
		}
	}, [user?.id, authUser?.id, isGuest]);

	useEffect(() => {
		initAnalytics();
	}, []);

	// track opening of shared link (if any) and remove URL param
	useEffect(() => {
		if (!hasProcessedSharedLink.current && sharedParam) {
			hasProcessedSharedLink.current = true;

			if (sharedParam === 'scene') {
				trackOutputAction('open_scene_link');
			} else if (sharedParam === 'frame') {
				trackOutputAction('open_frame_link');
			}

			// Remove the shared parameter from the URL to clean it up
			searchParams.delete('shared');
			const newUrl = `${location.pathname}${
				searchParams.toString() ? `?${searchParams.toString()}` : ''
			}`;
			window.history.replaceState({}, '', newUrl);
		}
	}, [sharedParam]);

	// Add version meta tag on mount
	useEffect(() => {
		const version = import.meta.env.VITE_GIT_SHA || 'development';
		const existingMeta = document.querySelector('meta[name="version"]');
		if (existingMeta) {
			existingMeta.setAttribute('content', version);
		} else {
			const meta = document.createElement('meta');
			meta.name = 'version';
			meta.content = version;
			document.head.appendChild(meta);
		}
	}, []);

	// Handle initial sidebarVisible logic on first load
	useEffect(() => {
		if (!isAppReady || sidebarVisibilityInitialized) return;

		let shouldShowSidebar = true;

		if (isBaseRoute && !hasProjects) {
			// Base route with no projects -> hide sidebar
			shouldShowSidebar = false;
		} else if (isSceneRoute) {
			// First route is project route -> hide sidebar
			shouldShowSidebar = false;
		}

		dispatch(setSidebarVisible(shouldShowSidebar));
	}, [isAppReady, isBaseRoute, isSceneRoute, hasProjects, sidebarVisibilityInitialized, dispatch]);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	// remove scrollbars
	document.body.style.overflow = 'hidden';

	if (!authUser) {
		return (
			<ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
				<S.Base
					ref={baseRef}
					sidebarVisible={false}
					sidebarDocked={false}
					isMobile={isMobile}
					sidebarWidth={sidebarWidth}
				>
					<LoadingOverlay />
				</S.Base>
			</ThemeProvider>
		);
	}

	// bypass invite code page for frame route (we don't want to block people who are just looking at frames and haven't been invited yet)
	if (
		!hasAccess &&
		!location.pathname.startsWith('/frame/') &&
		!location.pathname.startsWith('/project/')
	) {
		return (
			<ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
				<InviteCode email={authUser.email} />
			</ThemeProvider>
		);
	}

	return (
		<DndProvider backend={HTML5Backend}>
			<ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
				<>
					{!isMobile && !isGuest && <SidebarToggle isFloating />}
					<GlobalContextMenu>
						<S.Base
							ref={baseRef}
							isMobile={isMobile}
							sidebarVisible={sidebarVisible}
							sidebarDocked={sidebarDocked}
							sidebarWidth={sidebarWidth}
						>
							<Sidebar />
							<S.MainContent
								isMobile={isMobile}
								sidebarVisible={sidebarVisible}
								sidebarWidth={sidebarWidth}
							>
								<Routes>
									<Route path="/" element={<RouteToReduxSyncer />}>
										<Route path="/" element={<ProjectCreator />} />
										<Route path={routes.profilePattern} element={<Profile />} />
										<Route
											path={routes.scenePattern}
											element={<SceneLayout frameId={frameIdParam} />}
										/>
										<Route path={routes.framePattern} element={<StandaloneFrameViewer />} />
										<Route path={routes.designSystemPattern} element={<DesignSystemLayout />} />
										<Route
											path={routes.createDesignSystemPattern}
											element={<DesignSystemCreator />}
										/>
										<Route
											path="*"
											element={<ErrorOverlay text="Page not found" showProjectsLink />}
										/>
									</Route>
								</Routes>
								{loading && <LoadingOverlay />}
							</S.MainContent>
							<StaleAppDetector />
							<ModalManager />
							{sceneLoadError && <ErrorOverlay text={sceneLoadError} />}
						</S.Base>
					</GlobalContextMenu>
					<ToastContainer position="bottom-right" closeOnClick pauseOnHover />
				</>
			</ThemeProvider>
		</DndProvider>
	);
}

function RouteToReduxSyncer() {
	const dispatch = useDispatch();
	const params = useParams();
	const location = useLocation();
	const lastTrackedUrl = useRef<string>('');

	// Keep track of the active scene id in redux.
	useEffect(() => {
		dispatch(setActiveSceneId(params.sceneId ?? null));
	}, [params.sceneId, dispatch]);

	// Keep track of the active design system id in redux.
	useEffect(() => {
		dispatch(setActiveDesignSystemId(params.designSystemId ?? null));
	}, [params.designSystemId, dispatch]);

	// Track page views when route changes
	useEffect(() => {
		const currentUrl = location.pathname + location.search;
		if (currentUrl !== lastTrackedUrl.current) {
			lastTrackedUrl.current = currentUrl;
			trackPageView(currentUrl);
		}
	}, [location.pathname, location.search]);

	// Only render nested route(s)
	return <Outlet />;
}

function App() {
	return (
		<BrowserRouter>
			<AppContent />
		</BrowserRouter>
	);
}

export default App;
